import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, FileBadge, Loader2, Lock, Upload, ExternalLink, FileText, Printer } from 'lucide-react';
import { toast } from 'sonner';
import SaveSnapshotButton from './SaveSnapshotButton';
import { printHtml } from '../lib/print';
import DocumentHeader from './DocumentHeader';
import DocumentImportDialog from './DocumentImportDialog';
import DocumentViewerDialog from './DocumentViewerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* PropertyPermitsTableDialog — replaces the older list-based permits
   dialog with a structured Apple-style table.
     Columns: # · Name · Valid from · Valid until · Issuing entity · Reference Nr · Scan
   Inline-editable for admins; locked card for non-admins. */
export default function PropertyPermitsDialog({ open, onClose, hasFullAccess, adminToken }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [docName, setDocName] = useState('Property Licenses & Permits Register');
  const [importOpen, setImportOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState({ url: '', title: '' });
  const fileInputRefs = useRef({});

  const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : {};

  const load = useCallback(async () => {
    if (!hasFullAccess) return;
    try {
      const { data } = await axios.get(`${API}/property-permits-table`, auth);
      setRows(data.rows || []);
    } catch (_) { /* noop */ }
  }, [hasFullAccess, adminToken]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/property-permits-table`, { rows: next }, auth);
      setRows(data.rows || next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (idx, field, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  // Save when an input loses focus (debounced via blur). Avoids saving
  // on every keystroke which would be a chatty back-and-forth.
  const commit = () => persist(rows);

  const addRow = () => {
    const next = [...rows, { id: '', name: '', valid_from: '', valid_until: '', issuing_entity: '', reference_nr: '', scan_url: '' }];
    setRows(next);
  };

  const removeRow = async (idx) => {
    if (!window.confirm(`Remove "${rows[idx]?.name || 'this license'}"?`)) return;
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    persist(next);
  };

  const uploadScan = async (idx, file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error('Scan too large (max 12MB).'); return; }
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // iter-79: keep_raw preserves the original PDF (vs the default
      // behaviour which converts a multi-page PDF down to a single PNG
      // of page 1). Licenses are routinely 3-10 pages — losing pages
      // is what made them look "broken" in the viewer.
      fd.append('keep_raw', 'true');
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        ...auth,
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url.startsWith('http') ? data.url : `${BACKEND}${data.url}`;
      const next = rows.map((r, i) => (i === idx ? { ...r, scan_url: url } : r));
      setRows(next);
      await persist(next);
      toast.success('Scan uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingIdx(null);
    }
  };

  // Expiry highlight — Three tiers per user spec:
  //   • EXPIRED                     → solid red badge
  //   • Within 90 days (3 months)   → "🚨 Expires soon" pulsing red glow
  //   • Within 60 days              → kept as amber for finer granularity
  // The pulsing red glow at 90 days is what the user asked for on the
  // licenses screen so admins SEE the looming expiry immediately.
  const expiryClass = (validUntil) => {
    if (!validUntil) return null;
    try {
      const due = new Date(validUntil);
      const today = new Date();
      const days = (due - today) / (1000 * 60 * 60 * 24);
      if (days < 0) return { color: '#DC2626', label: 'Expired', glow: true };
      if (days <= 90) return { color: '#DC2626', label: `${Math.ceil(days)}d left`, glow: true };
      if (days <= 180) return { color: '#B45309', label: `${Math.ceil(days)}d left`, glow: false };
      return null;
    } catch (_) { return null; }
  };

  if (!open) return null;

  if (!hasFullAccess) {
    return (
      <div data-testid="property-permits-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
        <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
          <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #003DA5 100%)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-white" />
                <p className="text-base font-bold text-white" style={{ fontFamily: 'Manrope' }}>Admin / Engineer Access Required</p>
              </div>
              <button data-testid="property-permits-close" onClick={onClose} type="button" aria-label="Close" className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}>
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
          </div>
          <div className="p-6 text-center">
            <FileBadge className="w-12 h-12 mx-auto mb-3" style={{ color: '#003DA5' }} />
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>Property Licenses & Permits</h3>
            <p className="text-sm mb-4" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              Sign in with the right credentials to view the property's business licenses, building permits, fire certificates, and shared-drive links.
            </p>
            <button onClick={() => { window.location.href = '/admin'; }} className="px-5 py-2.5 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="property-permits-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#0E1F2C', borderColor: '#1F2D3D' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#A3D4D4' }}>
              <FileBadge className="w-5 h-5" style={{ color: '#0E1F2C' }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>Property Compliance</p>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>Licenses &amp; Permits Register</h2>
            </div>
          </div>
          <button data-testid="property-permits-close" onClick={onClose} type="button" aria-label="Close" className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}>
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Action toolbar — Save snapshot + Print live above the table so they're
            discoverable next to the dialog header. */}
        {hasFullAccess && (
          <div className="flex items-center justify-end gap-2 px-5 py-2 border-b" style={{ borderColor: '#EAE5D9', backgroundColor: '#F7F2E8' }}>
            <button
              type="button"
              data-testid="permits-restore-missing"
              title="Re-fetch any permit scans that have gone missing from local disk"
              onClick={async () => {
                if (!window.confirm('Try to restore any missing permit scans from Object Storage?')) return;
                try {
                  const { data } = await axios.post(`${API}/admin/permits/restore-missing-scans`, {}, auth);
                  const msg = `Checked ${data.checked} files: ${data.on_disk} on disk · ${data.restored} restored · ${data.lost} unrecoverable`;
                  if (data.lost > 0) {
                    toast.warning(msg, { duration: 8000 });
                  } else {
                    toast.success(msg);
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Restore failed');
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', fontFamily: 'Manrope' }}
            >
              <FileBadge className="w-3 h-3" /> Restore missing
            </button>
            <button
              type="button"
              data-testid="permits-import-btn"
              title="Import a PDF / Word / Excel / CSV into this register"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #1E40AF 100%)', fontFamily: 'Manrope' }}
            >
              <Upload className="w-3 h-3" /> Import file
            </button>
            <button
              type="button"
              onClick={() => {
                const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                const body = `
                  <p class="kicker">DREAMS ROSE HALL · LICENSES &amp; PERMITS</p>
                  <h1>${esc(docName || 'Property Permits Register')}</h1>
                  <p class="meta">${rows.length} record${rows.length === 1 ? '' : 's'} · Printed ${esc(new Date().toLocaleString())}</p>
                  <table>
                    <thead><tr><th>#</th><th>Name</th><th>Valid from</th><th>Valid until</th><th>Issuing entity</th><th>Reference Nr</th></tr></thead>
                    <tbody>${rows.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.name)}</td><td>${esc(r.valid_from)}</td><td>${esc(r.valid_until)}</td><td>${esc(r.issuing_entity)}</td><td>${esc(r.reference_nr)}</td></tr>`).join('')}</tbody>
                  </table>
                `;
                printHtml(body, docName || 'Property Permits Register');
              }}
              data-testid="permits-print"
              title="Print the permits register"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white"
              style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }}
            >
              <Printer className="w-3 h-3" /> Print
            </button>
            <SaveSnapshotButton
              kind="permit"
              defaultTitle={docName || `Licenses & Permits · ${new Date().toLocaleDateString()}`}
              getPayload={() => ({ rows, doc_name: docName })}
              testid="permits-save-snapshot"
            />
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {/* Branded centered banner + editable document name. */}
          {hasFullAccess && (
            <div className="px-4 pt-3">
              <DocumentHeader
                scopeId="permits"
                autoName="Property Licenses & Permits Register"
                onChange={setDocName}
                readOnly={!hasFullAccess}
              />
            </div>
          )}
          {/* Expiring-soon banner — pulsing red so admins can't ignore it.
              Counts every row with ≤ 90 days remaining; clicking it
              scrolls to the first such row. */}
          {(() => {
            const soon = rows.filter((r) => {
              const ex = expiryClass(r.valid_until);
              return ex?.glow;
            });
            if (soon.length === 0) return null;
            return (
              <div
                data-testid="permit-expiry-banner"
                onClick={() => {
                  const first = soon[0];
                  const el = document.querySelector(`[data-testid='permit-row-${rows.findIndex((r) => r === first)}']`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="mx-4 mt-3 mb-1 px-4 py-2.5 rounded-lg cursor-pointer flex items-center gap-3 permit-banner-glow"
                style={{ backgroundColor: '#FEE2E2', borderLeft: '4px solid #DC2626', fontFamily: 'Manrope' }}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DC2626', color: 'white', fontWeight: 800 }}>!</div>
                <div className="text-xs flex-1">
                  <p className="font-bold text-red-700">
                    {soon.length} license{soon.length > 1 ? 's' : ''} expiring within 90 days
                  </p>
                  <p className="text-[11px] text-red-600/80 mt-0.5">
                    Click to jump to the soonest-expiring entry. Admins also receive a Resend email digest as expiry approaches.
                  </p>
                </div>
              </div>
            );
          })()}
          <style>{`
            @keyframes permitRowPulseRed {
              0%, 100% { background-color: rgba(220, 38, 38, 0.06); box-shadow: inset 4px 0 0 #DC2626; }
              50%      { background-color: rgba(220, 38, 38, 0.16); box-shadow: inset 4px 0 0 #B91C1C; }
            }
            .permit-row-glow-red { animation: permitRowPulseRed 2.4s ease-in-out infinite; }
            @keyframes permitBannerPulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45), inset 4px 0 0 #DC2626; }
              50%      { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); inset 4px 0 0 #DC2626; }
            }
            .permit-banner-glow { animation: permitBannerPulse 2s ease-in-out infinite; }
          `}</style>
          <table className="w-full text-sm" style={{ fontFamily: 'Manrope' }}>
            <thead style={{ backgroundColor: '#F5F0E1', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ borderBottom: '2px solid #003DA5' }}>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5', width: 36 }}>#</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>Name of License</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5', width: 130 }}>Valid From</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5', width: 130 }}>Valid Until</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>Issuing Entity</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5', width: 140 }}>Reference Nr</th>
                <th className="px-3 py-2.5 text-center text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5', width: 110 }}>Scan</th>
                <th className="px-3 py-2.5" style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12" style={{ color: '#5C6B6B' }}>
                    No licenses registered yet. Click "Add license" below to start.
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => {
                const exp = expiryClass(r.valid_until);
                return (
                  <tr
                    key={r.id || idx}
                    data-testid={`permit-row-${idx}`}
                    className={`hover:bg-amber-50/60 transition ${exp?.glow ? 'permit-row-glow-red' : ''}`}
                    style={{ borderBottom: '1px solid #EAE5D9' }}
                  >
                    <td className="px-3 py-2.5 text-xs font-bold" style={{ color: '#5C6B6B' }}>{idx + 1}</td>
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`permit-row-name-${idx}`}
                        value={r.name}
                        onChange={(e) => updateRow(idx, 'name', e.target.value)}
                        onBlur={commit}
                        placeholder="e.g. Tourism Operating Licence"
                        className="w-full px-2 py-1.5 rounded text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none transition"
                        style={{ color: '#1A2E2E' }}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`permit-row-from-${idx}`}
                        type="date"
                        value={r.valid_from || ''}
                        onChange={(e) => updateRow(idx, 'valid_from', e.target.value)}
                        onBlur={commit}
                        className="w-full px-2 py-1.5 rounded text-xs bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none transition"
                        style={{ color: '#1A2E2E' }}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <input
                          data-testid={`permit-row-until-${idx}`}
                          type="date"
                          value={r.valid_until || ''}
                          onChange={(e) => updateRow(idx, 'valid_until', e.target.value)}
                          onBlur={commit}
                          className="flex-1 px-2 py-1.5 rounded text-xs bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none transition"
                          style={{ color: '#1A2E2E' }}
                        />
                        {exp && (
                          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: exp.color }}>{exp.label}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`permit-row-entity-${idx}`}
                        value={r.issuing_entity}
                        onChange={(e) => updateRow(idx, 'issuing_entity', e.target.value)}
                        onBlur={commit}
                        placeholder="e.g. Jamaica Tourist Board"
                        className="w-full px-2 py-1.5 rounded text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none transition"
                        style={{ color: '#1A2E2E' }}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`permit-row-ref-${idx}`}
                        value={r.reference_nr}
                        onChange={(e) => updateRow(idx, 'reference_nr', e.target.value)}
                        onBlur={commit}
                        placeholder="REF-12345"
                        className="w-full px-2 py-1.5 rounded text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-blue-400 focus:bg-white outline-none transition"
                        style={{ color: '#1A2E2E' }}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      {r.scan_url ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            data-testid={`permit-row-scan-view-${idx}`}
                            onClick={() => setViewDoc({ url: r.scan_url, title: r.name || r.reference_nr || 'Permit document' })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold hover:scale-105 active:scale-95 transition-transform duration-75 cursor-pointer"
                            style={{ color: '#003DA5', backgroundColor: '#DBEAFE', touchAction: 'manipulation' }}
                          >
                            <FileText className="w-3 h-3" /> View
                          </button>
                          <button data-testid={`permit-row-scan-replace-${idx}`} onClick={() => fileInputRefs.current[idx]?.click()} className="p-1 rounded hover:bg-slate-100" title="Replace scan">
                            <Upload className="w-3 h-3" style={{ color: '#5C6B6B' }} />
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`permit-row-scan-upload-${idx}`}
                          onClick={() => fileInputRefs.current[idx]?.click()}
                          disabled={uploadingIdx === idx}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-bold w-full justify-center"
                          style={{ backgroundColor: '#003DA5', color: 'white' }}
                        >
                          {uploadingIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          {uploadingIdx === idx ? 'Up…' : 'Upload'}
                        </button>
                      )}
                      <input
                        ref={(el) => (fileInputRefs.current[idx] = el)}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadScan(idx, f); e.target.value = ''; }}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button data-testid={`permit-row-remove-${idx}`} onClick={() => removeRow(idx)} className="p-1.5 rounded hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals summary */}
        {rows.length > 0 && (() => {
          const soon = rows.filter((r) => expiryClass(r.valid_until)?.glow).length;
          const expired = rows.filter((r) => {
            if (!r.valid_until) return false;
            const d = new Date(r.valid_until);
            return !isNaN(d) && d < new Date();
          }).length;
          const withScans = rows.filter((r) => r.scan_url).length;
          return (
            <div
              data-testid="permits-totals"
              className="mx-5 my-2 p-3 rounded-xl flex flex-wrap items-center gap-4 text-xs"
              style={{ backgroundColor: '#EAF2FF', border: '1px solid #C9D6E6', fontFamily: 'Manrope' }}
            >
              <span className="font-bold uppercase tracking-wider text-[10px]" style={{ color: '#003DA5' }}>Totals</span>
              <span style={{ color: '#1A2E2E' }}>Total: <strong>{rows.length}</strong></span>
              <span style={{ color: '#15803D' }}>With scans: <strong>{withScans}</strong></span>
              <span style={{ color: '#D97706' }}>Expiring ≤90d: <strong>{soon}</strong></span>
              <span style={{ color: '#DC2626' }}>Expired: <strong>{expired}</strong></span>
            </div>
          );
        })()}

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t" style={{ backgroundColor: '#F5F0E1', borderColor: '#E2DBC8' }}>
          <button
            data-testid="permits-add-row"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
          >
            <Plus className="w-4 h-4" /> Add license / permit
          </button>
          <span className="text-[11px]" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
            {saving ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span> : `${rows.length} record${rows.length === 1 ? '' : 's'} on file`}
          </span>
        </div>
      </div>

      {/* Universal document import — converts PDF / Word / Excel / CSV
          into the permits table. */}
      <DocumentImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        targetType="permits"
        activeListName="Permits Register"
        newNameDefault="Imported permits"
        onCommit={async (importedRows, { mode }) => {
          const next = mode === 'merge' ? [...rows, ...importedRows] : importedRows;
          await persist(next);
        }}
      />

      {/* In-app document viewer — used when admins click "View" on an
          uploaded permit/license scan. Replaces the old
          `target="_blank"` raw file link, which on mobile Safari
          opened the PDF in a chromeless tab with no obvious close. */}
      <DocumentViewerDialog
        open={!!viewDoc.url}
        url={viewDoc.url}
        title={viewDoc.title}
        onClose={() => setViewDoc({ url: '', title: '' })}
      />
    </div>
  );
}
